import React from 'react';
import { Card } from '@/components/ui/card';
import {
  Globe, UserCog, Lock, ListOrdered,
  CheckCircle2, CreditCard, AlertTriangle,
} from 'lucide-react';
import { bruneiTodayPretty, bruneiTomorrowPretty } from './chipConfig';

// Single Glance card used by both Today and Tomorrow with different
// segments. Keeps the markup DRY and the tests stable.

const PALETTE = {
  emerald: 'text-emerald-700 hover:bg-emerald-100',
  blue: 'text-blue-700 hover:bg-blue-100',
  slate: 'text-slate-700 hover:bg-slate-200',
  amber: 'text-amber-700 hover:bg-amber-100',
  red: 'text-red-700 hover:bg-red-100',
};

function GlanceCard({
  cardClass, accentClass, label, prettyDate, total,
  trailingFlag, segments, onSegment, segmentTestPrefix, dateTestId, testId,
}) {
  return (
    <Card className={`p-4 mb-4 ${cardClass}`} data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className={`text-[10px] uppercase tracking-widest font-bold ${accentClass}`}>{label}</div>
          <div className="text-sm font-semibold text-slate-700" data-testid={dateTestId}>{prettyDate}</div>
          <div className="text-xs text-muted-foreground">
            · {total} active booking{total === 1 ? '' : 's'}
          </div>
          {trailingFlag}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {segments.map(({ key, label: segLabel, icon: Icon, color, hideIfZero, count }) => {
            if (hideIfZero && count === 0) return null;
            return (
              <button
                key={key}
                type="button"
                onClick={() => onSegment(key)}
                data-testid={`${segmentTestPrefix}-stat-${key.toLowerCase()}`}
                title={`View ${label.toLowerCase()}'s ${segLabel} bookings`}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold transition-colors ${PALETTE[color]}`}
              >
                <Icon className="w-4 h-4" />
                <span className="tabular-nums">{count}</span>
                <span className="font-semibold text-xs uppercase tracking-wide">{segLabel}</span>
              </button>
            );
          })}
        </div>
      </div>
    </Card>
  );
}

export function TodayGlance({ stats, onApply }) {
  const segments = [
    { key: 'CUSTOMER', label: 'online', icon: Globe, color: 'emerald', count: stats.CUSTOMER },
    { key: 'MANUAL_STAFF', label: 'manual', icon: UserCog, color: 'blue', count: stats.MANUAL_STAFF },
    { key: 'STAFF_BLOCK', label: 'blocks', icon: Lock, color: 'slate', count: stats.STAFF_BLOCK },
    { key: 'WAITLIST_CONVERSION', label: 'waitlist', icon: ListOrdered, color: 'amber', count: stats.WAITLIST_CONVERSION },
  ];
  return (
    <GlanceCard
      cardClass="border-l-4 border-l-emerald-500 bg-gradient-to-r from-emerald-50/60 to-white"
      accentClass="text-emerald-700"
      label="Today"
      prettyDate={bruneiTodayPretty()}
      total={stats.total}
      segments={segments}
      onSegment={onApply}
      segmentTestPrefix="today"
      dateTestId="today-date-label"
      testId="today-at-a-glance"
    />
  );
}

export function TomorrowPreview({ stats, onApply }) {
  const segments = [
    { key: 'CONFIRMED', label: 'confirmed', icon: CheckCircle2, color: 'emerald', count: stats.CONFIRMED },
    { key: 'AWAITING_PAYMENT', label: 'awaiting payment', icon: CreditCard, color: 'amber', count: stats.AWAITING_PAYMENT },
    { key: 'PAYMENT_FAILED', label: 'failed', icon: AlertTriangle, color: 'red', count: stats.PAYMENT_FAILED, hideIfZero: true },
    { key: 'STAFF_BLOCK', label: 'blocks', icon: Lock, color: 'slate', count: stats.STAFF_BLOCK, hideIfZero: true },
  ];
  const trailingFlag = stats.AWAITING_PAYMENT > 0 ? (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200 text-[10px] font-bold uppercase tracking-wide"
      data-testid="tomorrow-chase-flag"
      title="Bookings still need payment before tomorrow"
    >
      <AlertTriangle className="w-3 h-3" />
      Chase {stats.AWAITING_PAYMENT}
    </span>
  ) : null;
  return (
    <GlanceCard
      cardClass="border-l-4 border-l-indigo-400 bg-gradient-to-r from-indigo-50/50 to-white"
      accentClass="text-indigo-700"
      label="Tomorrow"
      prettyDate={bruneiTomorrowPretty()}
      total={stats.total}
      trailingFlag={trailingFlag}
      segments={segments}
      onSegment={onApply}
      segmentTestPrefix="tomorrow"
      dateTestId="tomorrow-date-label"
      testId="tomorrow-preview"
    />
  );
}
